/*
 * @Author: youfanrong 737064439@qq.com
 * @Date: 2025-05-14 12:26:56
 * @LastEditors: youfanrong 737064439@qq.com
 * @LastEditTime: 2025-09-02 17:18:40
 * @FilePath: /bar-front-end/src/utils/wsModule.ts
 */

import { useUserStoreWithOut } from '@/store/modules/user'
import router from '@/router'
import {
  print,
  onElectronMessage,
  isInElectron,
  testMessageListening,
  testConnectionCheck
} from '@/utils/electronBridge'
import { ElNotification, ElButton } from 'element-plus'
import { h } from 'vue'
import { printOrderApi } from '@/api/order'

class WsModule {
  ws: any = null
  token: string | null = null
  wsUrl: string = import.meta.env.VITE_APP_WS_URL
  reconnectAttempts: number = 0
  maxReconnectAttempts: number = 5
  reconnectTimer: any = null

  constructor() {
    // 延迟初始化，确保 DOM 和 electronBridge 都已准备好
    if (typeof window !== 'undefined') {
      // 如果在浏览器环境中，等待 DOM 加载完成
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          this.initElectronMessageListener()
        })
      } else {
        // DOM 已经加载完成，延迟一点时间确保 electronBridge 准备好
        setTimeout(() => {
          this.initElectronMessageListener()
        }, 100)
      }
    } else {
      // 非浏览器环境，直接初始化
      this.initElectronMessageListener()
    }
  }

  // 初始化 Electron 消息监听
  initElectronMessageListener() {
    console.log('=== WsModule 初始化 Electron 消息监听 ===')
    console.log('当前时间:', new Date().toISOString())
    console.log('document.readyState:', document.readyState)

    // 先测试 electronBridge 的状态
    testMessageListening()

    console.log('isInElectron:', isInElectron())
    if (isInElectron()) {
      console.log('在 Electron 环境中，开始监听消息')
      onElectronMessage((message) => {
        console.log('=== WsModule 收到 Electron 消息 ===')
        console.log('消息内容：', message)
        console.log('消息类型：', typeof message)
        this.handleElectronMessage(message)
      })

      // 添加额外的测试方法
      console.log('消息监听设置完成，可以通过以下方式测试：')
      console.log('1. window.wsModule.testElectronMessage()')
      console.log('2. 发送 check-websocket-connection 消息')
    } else {
      console.log('不在 Electron 环境中，跳过消息监听')
    }
  }

  // 处理 Electron 消息
  handleElectronMessage(message: any) {
    console.log('处理 Electron 消息：', message, typeof message)

    // 如果消息是字符串，尝试解析
    let parsedMessage = message
    if (typeof message === 'string') {
      try {
        parsedMessage = JSON.parse(message)
      } catch (e) {
        // 如果不是 JSON，直接使用字符串值
        parsedMessage = { action: message }
      }
    }

    // 检查消息类型
    if (
      parsedMessage.action === 'check-websocket-connection' ||
      message === 'check-websocket-connection'
    ) {
      console.log('收到检查 WebSocket 连接的消息，准备重连')
      this.checkAndReconnectWebSocket()
    }
  }

  // 检查 WebSocket 连接状态并重连
  checkAndReconnectWebSocket() {
    console.log('检查 WebSocket 连接状态...')

    // 检查当前连接状态
    if (
      !this.ws ||
      this.ws.readyState === WebSocket.CLOSED ||
      this.ws.readyState === WebSocket.CLOSING
    ) {
      console.log('WebSocket 连接已断开，开始重连...')
      this.reconnectWebSocket()
    } else if (this.ws.readyState === WebSocket.CONNECTING) {
      console.log('WebSocket 正在连接中...')
    } else if (this.ws.readyState === WebSocket.OPEN) {
      console.log('WebSocket 连接正常')
      // 连接正常，但为了确保稳定性，也可以选择重连
      // 这里根据实际需求决定是否要强制重连
      console.log('强制重连以确保连接稳定性')
      this.reconnectWebSocket()
    }
  }

  // 重连 WebSocket
  reconnectWebSocket() {
    console.log('开始重连 WebSocket...')

    // 先关闭现有连接
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }

    // 清除重连定时器
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    // 重置重连次数
    this.reconnectAttempts = 0

    // 重新连接
    this.connectWebSocket()
  }

  // 提供一个测试方法，方便调试
  testElectronMessage() {
    console.log('测试 Electron 消息处理')
    this.handleElectronMessage('check-websocket-connection')
  }

  // 手动重新初始化消息监听（调试用）
  reinitMessageListener() {
    console.log('手动重新初始化消息监听')
    this.initElectronMessageListener()
  }

  // 获取当前状态（调试用）
  getStatus() {
    return {
      wsState: this.ws ? this.ws.readyState : 'null',
      isInElectron: isInElectron(),
      electronBridge: !!window.electronBridge,
      electronBridgeMethods: window.electronBridge ? Object.keys(window.electronBridge) : []
    }
  }

  // 测试连接检查（调试用）
  testConnectionCheck() {
    console.log('WsModule 测试连接检查')
    testConnectionCheck()
  }

  // 连接WebSocket
  connectWebSocket() {
    const userStore = useUserStoreWithOut()
    const token = userStore.token
    this.token = token
    console.log('token=======', this.ws, token)
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('WebSocket 已连接，跳过重复连接')
      return // 防止重复连接
    }
    if (!token) {
      console.log('token为空，无法连接WebSocket')
      this.closeWebSocket()
      // 跳转登录页面
      router.push('/login')
      return
    }
    console.log('连接中')
    this.ws = new WebSocket(`${this.wsUrl}/barV2/frontend/ws?token=${token}`)
    // this.ws = new WebSocket(`ws://front.v2.sounding-club.cn/barV2/frontend/ws?token=${token}`)

    this.ws.onopen = () => {
      console.log('WebSocket 连接成功')
      // 重置重连次数
      this.reconnectAttempts = 0
      // 清除重连定时器
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer)
        this.reconnectTimer = null
      }
    }

    this.ws.onmessage = (data) => {
      console.log('收到消息：', data)
      this.handleMessage(data)
    }

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error)
    }

    this.ws.onclose = (event) => {
      console.log('WebSocket 连接关闭', event)
      if (event.code === 1000) {
        console.log('WebSocket 主动关闭')
        this.ws = null
      } else {
        // 仅在非主动关闭时尝试重连
        console.log('尝试重连...')
        // 重连逻辑
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectTimer = setTimeout(() => {
            console.log(`尝试重连 ${this.reconnectAttempts + 1}`)
            this.connectWebSocket()
            this.reconnectAttempts++
          }, 5000)
        } else {
          console.log('达到最大重连次数，停止重连')
          this.ws = null
        }
      }
    }
  }

  // 关闭WebSocket
  closeWebSocket() {
    if (this.ws) {
      this.ws.close(1000, '主动关闭')
      this.ws = null
    }
  }

  // 打印订单
  printOrder(data) {
    console.log('打印数据：', data)
  }

  // 处理消息
  async handleMessage(data) {
    console.log('收到的websocket数据：', data)
    const dataObj = JSON.parse(data.data)
    switch (dataObj.msgType) {
      case 0: // 纯文字提示
        console.log('纯文字提示：', dataObj)
        break
      case 1: // 有待出票订单（下单，或者点击打印时）
        {
          console.log('打印数据：', dataObj)
          // 打印订单
          await print({ ...dataObj.data, msgType: dataObj.msgType })

          // 通知后端消息接受成功，标记为已出票 就会继续给你发消息打印（这个接口调用成功后出品部门的订单就会被标记为已出票）
          setTimeout(async () => {
            // 等待1秒后调用,否则会失败
            const res = await printOrderApi({
              pdepOrderId: dataObj.data.pdepOrderId
            })
            if (res.code === 200) {
              console.log('出单成功')
            }
          }, 1000)
        }
        break
      case 2: // 取酒订单打印
        {
          console.log('取酒订单打印数据：', dataObj)
          // 打印订单
          await print({ ...dataObj.data, msgType: dataObj.msgType })
        }
        break
      case 3: // 小程序取酒申请触发
        {
          const notification = ElNotification({
            title: '取酒申请',
            // message: '有新的取酒申请，请及时审核',
            message: () =>
              h(
                'div',
                {
                  style: {}
                },
                {
                  default: () => [
                    h(
                      'div',
                      {
                        style: {
                          marginBottom: '10px'
                        }
                      },
                      '有新的取酒申请，请及时审核'
                    ),
                    h(
                      ElButton,
                      {
                        type: 'primary',
                        onClick: () => {
                          router.push('/save-take-wine/take-wine-list')
                          // 关闭ElNotification
                          notification.close()
                        }
                      },
                      {
                        default: () => '去审核'
                      }
                    )
                  ]
                }
              ),
            duration: 0,
            type: 'warning'
          })
        }
        break

      case 4: // 打印结账单
        {
          console.log('打印结账单数据：', dataObj)
          const data = {
            printerName: 'test_p_zd',
            printerIp: '192.168.31.43',
            baseInfo: {
              orderNumber: '1234567890',
              boothName: 'test_booth',
              openTime: '2025-08-24 10:00:00',
              checkoutTime: '2025-08-24 10:00:00',
              bookerName: 'test_booker'
            },
            drinkDetail: {
              payDrinks: [
                {
                  drinkName: 'test_drink11111',
                  count: 1,
                  unit: '瓶',
                  price: 100,
                  amount: 100,
                  remark: 'test_remark'
                },
                {
                  drinkName: 'test_drink',
                  count: 1,
                  unit: '瓶',
                  price: 100,
                  amount: 100,
                  remark: 'test_remark'
                },
                {
                  drinkName: 'test_drink',
                  count: 1,
                  unit: '瓶',
                  price: 100,
                  amount: 100,
                  remark: 'test_remark'
                }
              ],
              giftDrinks: [
                {
                  drinkName: 'test_drink',
                  count: 1,
                  unit: '瓶',
                  price: 100,
                  amount: 100,
                  remark: 'test_remark'
                }
              ]
            },
            receivePayStat: {
              consumeAmount: 100,
              giftAmount: 100,
              minConsume: 100,
              minConsumeDiff: 100,
              actualReceive: 100,
              cashierName: 'test_cashier'
            },
            payMethod: {
              items: [
                {
                  typeName: 'test_type',
                  payAmount: 100,
                  voucher: 'test_voucher'
                }
              ],
              totalRecAmount: 100
            }
          }
          // 打印订单
          await print({ ...dataObj.data, msgType: dataObj.msgType })
          // await print({ ...data, msgType: dataObj.msgType })
        }
        break
      case 5: // 打印存酒单
        {
          console.log('打印存酒单数据：', dataObj)
          await print({ ...dataObj.data, msgType: dataObj.msgType })
        }
        break
      default:
        break
    }
  }
}

const wsModuleInstance = new WsModule()

// 在开发环境下将实例挂载到 window 对象上，方便调试
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  ;(window as any).wsModule = wsModuleInstance
  // 也挂载一些测试函数
  ;(window as any).testConnectionCheck = testConnectionCheck
  ;(window as any).testMessageListening = testMessageListening
  console.log('WsModule 实例已挂载到 window.wsModule，可用于调试')
  console.log('测试函数已挂载：window.testConnectionCheck, window.testMessageListening')
}

export default wsModuleInstance
